import { v4 as uuidv4 } from 'uuid'
import ApiClient from 'api-client/ApiClient'
import { sleep } from 'api-client/helpers'

// =============================================================================
// Types
// =============================================================================

interface MountIsoParams {
  vmId: string
  isoImageId: string
  vmName: string
  isoImageSizeBytes?: number
  // FR MID-FR-003: Cinder volume type for the ISO backing volume. The dialog defaults
  // this to the tenant's default type (replacing the old __DEFAULT__ behavior). When
  // empty/undefined we leave volume_type off the request so nothing invalid is sent.
  volumeType?: string
}

interface UnmountIsoParams {
  vmId: string
  volumeId: string
}

// =============================================================================
// API Client Helper
// =============================================================================

const getClients = () => {
  const { nova, cinder } = ApiClient.getInstance()
  return { nova, cinder }
}

// =============================================================================
// Polling Helpers
// =============================================================================

const DEFAULT_TIMEOUT_MS = 300000 // 5 minutes
const DEFAULT_POLL_INTERVAL_MS = 5000 // 5 seconds

async function pollUntil<T>(
  fn: () => Promise<T | null>,
  options: { timeoutMs?: number; pollIntervalMs?: number; timeoutMessage: string },
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, pollIntervalMs = DEFAULT_POLL_INTERVAL_MS } = options
  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    try {
      const result = await fn()
      if (result !== null) {
        return result
      }
    } catch (error) {
      if (error?.isFatal) {
        throw error // preserve fatal behavior
      }
      // ignore transient errors
    }
    await sleep(pollIntervalMs)
  }

  throw new Error(options.timeoutMessage)
}

// =============================================================================
// Volume Helpers
// =============================================================================

/**
 * Fetch display names for a list of volume IDs.
 * Returns a map of volumeId -> name (only populated where name is available).
 */
export async function getVolumeNames(ids: string[]): Promise<Record<string, string>> {
  const { cinder } = getClients()
  const results = await Promise.allSettled(ids.map((id) => cinder.getVolume(id)))
  const names: Record<string, string> = {}
  results.forEach((result, i) => {
    if (result.status === 'fulfilled' && result.value?.name) {
      names[ids[i]] = result.value.name
    }
  })
  return names
}

// =============================================================================
// Mount ISO Operations
// =============================================================================

/**
 * Mount an ISO image to a VM as a virtual CDROM.
 * Creates a volume from the ISO and attaches it with tag='cdrom'.
 */
export async function mountISO({
  vmId,
  isoImageId,
  vmName,
  isoImageSizeBytes,
  volumeType,
}: MountIsoParams) {
  const { nova, cinder } = getClients()

  // Calculate volume size from image size
  const bytesPerGiB = 1024 * 1024 * 1024
  const parsedSize = Number(isoImageSizeBytes)
  const sizeGiB =
    Number.isFinite(parsedSize) && parsedSize > 0 ? Math.ceil(parsedSize / bytesPerGiB) : 1

  // Create volume from ISO image.
  // FR MID-FR-003/MID-FR-004: only send volume_type when one is selected, so an empty
  // value never reaches Cinder.
  const volume = await cinder.createVolume({
    volume: {
      imageRef: isoImageId,
      size: Math.max(1, sizeGiB),
      name: `iso-mount-${vmName || vmId}-${uuidv4().slice(0, 4)}`,
      metadata: { volume_role: 'extra_drive' },
      volume_type: volumeType || undefined,
    },
  })

  if (!volume?.id) {
    throw new Error('Failed to create ISO volume')
  }

  // Wait for volume to be ready
  await pollUntil(
    async () => {
      const vol = await cinder.getPf9Volume({ id: volume.id })
      const status = vol?.status?.toLowerCase()

      if (status === 'available') return vol
      if (status?.startsWith('error')) {
        const error: any = new Error(`ISO volume failed with status "${status}"`)
        error.isFatal = true
        error.failedVolumeId = volume.id
        throw error
      }
      return null
    },
    {
      timeoutMs: 900000, // 15 minutes for large ISOs
      pollIntervalMs: 15000,
      timeoutMessage: `ISO volume is not ready to attach after waiting ${Math.round(
        900000 / 60000,
      )} minutes. Please try again.`,
    },
  )

  // Attach volume to VM
  try {
    await nova.attachVolume({
      vmId,
      body: {
        volumeAttachment: {
          volumeId: volume.id,
          tag: 'extra_drive',
          delete_on_termination: true,
        },
      },
    })
  } catch (error) {
    // Cleanup on failure
    try {
      await cinder.deleteVolume(volume.id)
    } catch {
      // Ignore cleanup errors
    }
    throw error
  }

  // Wait for the attachment to be confirmed in nova, then sleep to give the
  // CQRS backend time to process the nova event before the completion event
  // triggers a VM list refresh (mirrors the natural ~3s delay in unmountISO).
  await pollUntil(
    async () => {
      const attachments = await nova.getVolumeAttachments({ vmId })
      const isAttached = attachments?.some((a: any) => a?.volumeId === volume.id)
      return isAttached ? true : null
    },
    {
      timeoutMs: 60000,
      pollIntervalMs: 3000,
      timeoutMessage: 'Volume attachment confirmation timed out after 1 minute',
    },
  )
  await sleep(3000)

  return { volumeId: volume.id }
}

// =============================================================================
// Unmount ISO Operations
// =============================================================================

/**
 * Unmount an ISO from a VM. Detaches the CDROM volume, then deletes it (PCD-8376).
 * IO-FR-003: if the detach fails we throw before touching the volume.
 */
export async function unmountISO({ vmId, volumeId }: UnmountIsoParams) {
  const { nova } = getClients()

  // Detach volume
  await nova.detachVolume({ vmId, volumeId })

  // Wait for detachment
  await pollUntil(
    async () => {
      const attachments = await nova.getVolumeAttachments({ vmId })
      const stillAttached = attachments?.some((a: any) => a?.volumeId === volumeId)
      return stillAttached ? null : true
    },
    {
      timeoutMs: 180000,
      pollIntervalMs: 3000,
      timeoutMessage: 'ISO detach did not complete after 3 minutes',
    },
  )

  // IO-FR-002: the drive is already off the VM here, so a cleanup problem never fails the unmount.
  try {
    await deleteDetachedIsoVolume(volumeId)
    return { success: true, cleanupFailed: false }
  } catch {
    return { success: true, cleanupFailed: true }
  }
}

/**
 * IO-FR-001: delete the ISO volume once Cinder shows it as free. Cinder refuses to delete a
 * volume that is still "detaching", so we wait for "available" first. A volume that is
 * already gone counts as cleaned up.
 */
async function deleteDetachedIsoVolume(volumeId: string) {
  const { cinder } = getClients()

  const state = await pollUntil(
    async () => {
      const vol = await cinder.getPf9Volume({ id: volumeId })
      if (!vol) return 'gone'
      return vol.status?.toLowerCase() === 'available' ? 'available' : null
    },
    {
      timeoutMs: 120000,
      pollIntervalMs: 3000,
      timeoutMessage: 'ISO volume did not become available for deletion after 2 minutes',
    },
  )
  if (state === 'gone') return

  try {
    await cinder.deleteVolume(volumeId)
  } catch (error) {
    if (error?.response?.status !== 404) throw error
  }
}
